import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalanceOutlined';
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined';
import PendingActionsIcon from '@mui/icons-material/PendingActionsOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmberOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAltOutlined';
import BlockIcon from '@mui/icons-material/BlockOutlined';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottomOutlined';
import MoneyOffIcon from '@mui/icons-material/MoneyOffOutlined';
import { getFinanceOverview } from '../../api/finance';
import { useBranchFilter } from '../../context/branchContext';
import { PageHeader } from '../../components/PageHeader';
import { KpiCard } from '../dashboard/KpiCard';
import { FinanceFilters, FinancePaymentStatus, LedgerTotal, Money } from '../../types/finance';
import { ApprovalStatusChip } from './ApprovalStatusChip';
import { FinanceFilterBar } from './FinanceFilterBar';
import {
  formatDate,
  formatMoney,
  formatMoneyList,
  PAYMENT_STATUS_COLOR,
  PAYMENT_STATUS_LABEL,
} from './financeFormat';

const totalsAsMoney = (
  rows: LedgerTotal[],
  field: 'totalWithTax' | 'amount' = 'totalWithTax',
): Money[] => {
  const byCode = new Map<string, Money>();
  for (const r of rows) {
    const key = r.currency?.code ?? '—';
    const bucket = byCode.get(key) ?? { currency: r.currency, amount: 0 };
    bucket.amount += r[field];
    byCode.set(key, bucket);
  }
  return [...byCode.values()];
};

const countAndValue = (count: number, totals: LedgerTotal[]) =>
  count === 0 ? '0' : `${count} · ${formatMoneyList(totalsAsMoney(totals))}`;

const reviewLink = (paymentId: number) => `/finance/confirmations?paymentId=${paymentId}`;

const methodAndReference = (p: { paymentMethod: string | null; referenceNumber: string | null }) =>
  [p.paymentMethod?.replace(/_/g, ' ').toLowerCase(), p.referenceNumber]
    .filter(Boolean)
    .join(' · ') || '—';

const STATUS_ORDER: FinancePaymentStatus[] = ['PAID', 'PARTIALLY_PAID', 'UNPAID', 'NO_FEES'];

/** Finance → Finance Dashboard (Phase 96; approval-first since Phase 97).
 * Balances are today's figures from each student's Payment Checklist, which
 * counts approved payments only; the date range applies to dated records
 * (payments, invoices, receipts). "Today" cards go by decision time. */
export const FinanceDashboardPage = () => {
  const navigate = useNavigate();
  const { selectedBranchId } = useBranchFilter();
  const [filters, setFilters] = useState<FinanceFilters>({});
  const params = { ...filters, branchId: selectedBranchId };
  const query = useQuery({
    queryKey: ['finance-dashboard', params],
    queryFn: () => getFinanceOverview(params),
    placeholderData: (prev) => prev,
  });
  const d = query.data;
  const studentsLink = (status?: FinancePaymentStatus) => {
    const search = new URLSearchParams();
    if (status) search.set('paymentStatus', status);
    if (filters.universityId) search.set('universityId', String(filters.universityId));
    if (filters.intakeId) search.set('intakeId', String(filters.intakeId));
    if (filters.paymentCategoryId)
      search.set('paymentCategoryId', String(filters.paymentCategoryId));
    const qs = search.toString();
    return `/finance/students${qs ? `?${qs}` : ''}`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={AccountBalanceIcon}
        color="#2E7D32"
        title="Finance Dashboard"
        subtitle="Payments awaiting approval, today's decisions, confirmed collections and outstanding fees."
      />
      <FinanceFilterBar value={filters} onChange={setFilters} />
      {query.isFetching && <LinearProgress sx={{ mb: 1 }} />}

      {query.isError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => query.refetch()}>
              Retry
            </Button>
          }
        >
          Couldn't load the finance dashboard.
        </Alert>
      )}

      {d && (
        <Stack spacing={3}>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={4}>
              <Card
                variant="outlined"
                sx={{ height: '100%', borderColor: 'warning.main', borderWidth: 2 }}
              >
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <PendingActionsIcon color="warning" />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Pending Payment Approvals
                    </Typography>
                  </Stack>
                  <Typography variant="h3" fontWeight={700} data-testid="pending-approvals-count">
                    {d.pendingConfirmations.count}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    {formatMoneyList(totalsAsMoney(d.pendingConfirmations.totals))}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    Awaiting finance review — not counted as paid until approved.{' '}
                    {d.pendingConfirmations.withSlip} with a slip uploaded.
                  </Typography>
                  <Button
                    component={RouterLink}
                    to="/finance/confirmations"
                    variant="contained"
                    color="warning"
                    size="small"
                    sx={{ mt: 1.5 }}
                  >
                    Review payments
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} lg={8}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <KpiCard
                    label="Payments approved today"
                    value={countAndValue(
                      d.decidedToday.approved.count,
                      d.decidedToday.approved.totals,
                    )}
                    icon={TaskAltIcon}
                    color="#2E7D32"
                    to="/finance/ledger"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <KpiCard
                    label="Payments rejected today"
                    value={countAndValue(
                      d.decidedToday.rejected.count,
                      d.decidedToday.rejected.totals,
                    )}
                    icon={BlockIcon}
                    color="#C62828"
                    to="/finance/ledger"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <KpiCard
                    label={
                      filters.from || filters.to
                        ? 'Total confirmed payments (date range)'
                        : 'Total confirmed payments'
                    }
                    value={formatMoneyList(totalsAsMoney(d.paymentsReceived))}
                    icon={PaymentsIcon}
                    color="#1B5E20"
                    to="/finance/ledger"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <KpiCard
                    label="Total outstanding fees"
                    value={formatMoneyList(d.students.outstanding)}
                    icon={WarningAmberIcon}
                    color="#E65100"
                    to={studentsLink()}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <KpiCard
                    label="Partially paid students"
                    value={d.students.statusCounts.PARTIALLY_PAID}
                    icon={HourglassBottomIcon}
                    color="#F9A825"
                    to={studentsLink('PARTIALLY_PAID')}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <KpiCard
                    label="Unpaid students"
                    value={d.students.statusCounts.UNPAID}
                    icon={MoneyOffIcon}
                    color="#B71C1C"
                    to={studentsLink('UNPAID')}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Card variant="outlined">
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  Pending approvals
                </Typography>
                <Button component={RouterLink} to="/finance/confirmations" size="small">
                  All pending ({d.pendingConfirmations.count})
                </Button>
              </Stack>
              {d.pendingConfirmations.oldest.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No payments are awaiting approval.
                </Typography>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small" aria-label="Pending approvals">
                    <TableHead>
                      <TableRow>
                        <TableCell>Student</TableCell>
                        <TableCell>Payment item</TableCell>
                        <TableCell align="right">Submitted amount</TableCell>
                        <TableCell>Method / reference</TableCell>
                        <TableCell>Submitted</TableCell>
                        <TableCell>Submitted by</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {d.pendingConfirmations.oldest.map((p) => (
                        <TableRow
                          key={p.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(reviewLink(p.id))}
                        >
                          <TableCell>
                            {p.student.name}{' '}
                            <Typography component="span" variant="caption" color="text.secondary">
                              {p.student.studentNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {p.category.name}
                            {p.type !== 'PAYMENT' ? ` (${p.type.toLowerCase()})` : ''}
                            {p.resubmittedFrom && (
                              <Typography
                                component="div"
                                variant="caption"
                                color="text.secondary"
                              >
                                Resubmission of {p.resubmittedFrom.paymentNumber}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {formatMoney(p.totalWithTax, p.currency)}
                          </TableCell>
                          <TableCell>{methodAndReference(p)}</TableCell>
                          <TableCell>{formatDate(p.createdAt)}</TableCell>
                          <TableCell>{p.recordedBy ?? '—'}</TableCell>
                          <TableCell>
                            <ApprovalStatusChip status={p.status} />
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              component={RouterLink}
                              to={reviewLink(p.id)}
                              onClick={(e) => e.stopPropagation()}
                            >
                              Review
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Students by payment status ({d.students.total})
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
                From each student's Payment Checklist
                {filters.paymentCategoryId
                  ? ' (selected category only)'
                  : ' (registration items, base + SSCL)'}{' '}
                — approved payments only, current balances, not limited by the date range.
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {STATUS_ORDER.map((status) => (
                  <Chip
                    key={status}
                    component={RouterLink}
                    to={studentsLink(status)}
                    clickable
                    color={PAYMENT_STATUS_COLOR[status]}
                    variant={status === 'NO_FEES' ? 'outlined' : 'filled'}
                    label={`${PAYMENT_STATUS_LABEL[status]}: ${d.students.statusCounts[status]}`}
                  />
                ))}
                <Chip
                  variant="outlined"
                  label={`With a payment pending approval: ${d.students.withPendingPayment}`}
                />
              </Stack>
              <Typography variant="body2" sx={{ mt: 1.5 }}>
                Required (incl. SSCL): <strong>{formatMoneyList(d.students.required)}</strong> ·
                Paid (approved): <strong>{formatMoneyList(d.students.paid)}</strong>
              </Typography>
            </CardContent>
          </Card>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Invoices ({d.invoices.count})
                  </Typography>
                  {d.invoices.byType.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No invoices in this period.
                    </Typography>
                  )}
                  {d.invoices.byType.map((g) => (
                    <Typography key={`${g.invoiceType}-${g.currency?.code}`} variant="body2">
                      {g.invoiceType ?? 'Other'}: {g.count} · {formatMoney(g.amount, g.currency)}
                      {g.totalLkr > 0
                        ? ` · LKR ${g.totalLkr.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                        : ''}
                    </Typography>
                  ))}
                  <Button component={RouterLink} to="/finance/invoices" size="small" sx={{ mt: 1 }}>
                    View invoices
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Receipts ({d.receipts.count})
                  </Typography>
                  <Typography variant="body2">{formatMoneyList(d.receipts.totals)}</Typography>
                  {d.adjustments.length > 0 && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                      sx={{ mt: 1 }}
                    >
                      Approved adjustments:{' '}
                      {d.adjustments
                        .map((a) => `${a.type.toLowerCase()} ${formatMoney(a.amount, a.currency)}`)
                        .join(' · ')}
                    </Typography>
                  )}
                  <Button component={RouterLink} to="/finance/receipts" size="small" sx={{ mt: 1 }}>
                    View receipts
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card variant="outlined">
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  Recent transactions
                </Typography>
                <Button component={RouterLink} to="/finance/ledger" size="small">
                  Payment ledger
                </Button>
              </Stack>
              {d.recentTransactions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No transactions match these filters.
                </Typography>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Payment</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Student</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell align="right">Total incl. SSCL</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {d.recentTransactions.map((t) => (
                        <TableRow key={t.id}>
                          <TableCell>{t.paymentNumber}</TableCell>
                          <TableCell>{formatDate(t.paymentDate)}</TableCell>
                          <TableCell>
                            <Link
                              component={RouterLink}
                              to={`/finance/students/${t.student.id}?paymentId=${t.id}`}
                            >
                              {t.student.name}
                            </Link>{' '}
                            <Typography component="span" variant="caption" color="text.secondary">
                              {t.student.studentNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {t.category.name}
                            {t.type !== 'PAYMENT' ? ` (${t.type.toLowerCase()})` : ''}
                          </TableCell>
                          <TableCell align="right">
                            {formatMoney(t.totalWithTax, t.currency)}
                          </TableCell>
                          <TableCell>
                            <ApprovalStatusChip status={t.status} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </CardContent>
          </Card>
        </Stack>
      )}
    </Box>
  );
};
